import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";
import moment from "moment";

import Pagination from "../../common/Pagination";
import RecordsPerPage from "../../common/RecordsPerPage";
import TableLoading from "../../common/Loading/table_loading";
import SearchableDropdown from "../../common/SearchableDropdown";
import { AppContexts } from "../../../providers";
import FilterPanel from "../Common/FilterPanel";
import usePath from "../Common/History";
import {
  emptyValue,
  formatBatteryLevel,
  formatTimestamp,
  getActivityLabel,
  getDeviceName,
  getRentalName,
} from "../../seam_monitoring/utils";

const toApiStart = (date) => moment(date).startOf("day").toISOString();
const toApiEnd = (date) => moment(date).endOf("day").toISOString();

const getReportEventTone = (activityType) => {
  if (activityType === "locked") return "locked";
  if (activityType === "unlocked") return "unlocked";
  if (["access_denied", "low_battery", "battery_status_changed", "connection_became_flaky", "connected_account_reauthorization_requested"].includes(activityType)) {
    return "warning";
  }
  if (["disconnected", "tampered", "device_removed", "device_deleted", "connected_account_disconnected", "connected_account_deleted"].includes(activityType)) {
    return "danger";
  }
  if (["connected", "connection_stabilized", "device_added", "connected_account_connected", "connected_account_completed_first_sync", "connected_account_successful_login"].includes(activityType)) {
    return "success";
  }
  return "neutral";
};

const SmartLockActivities = () => {
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rentalId, setRentalId] = useState("");
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  const { active, startDate, endDate } = useContext(AppContexts.ReportsContext);
  const { getMonitoringActivity } = useContext(AppContexts.SeamContext);
  const { rentals, get: getRentals } = useContext(AppContexts.RentalsContext);
  const didRequestRentals = useRef(false);
  const path = usePath();

  useEffect(() => {
    if (path !== active) {
      setPage(0);
      setFilter("");
      setRentalId("");
    }
  }, [path, active]);

  useEffect(() => {
    if ((!rentals || rentals.length === 0) && !didRequestRentals.current) {
      didRequestRentals.current = true;
      getRentals();
    }
  }, [rentals, getRentals]);

  const getFn = useCallback(
    async (start, end) => {
      if (!start || !end) return;

      setLoading(true);
      const rows = await getMonitoringActivity({
        rental_id: rentalId || undefined,
        start_date: toApiStart(start),
        end_date: toApiEnd(end),
        limit: 500,
      });
      setActivity(rows);
      setLoading(false);
    },
    [getMonitoringActivity, rentalId]
  );

  useEffect(() => {
    if (active === "smart-lock-activities") {
      getFn(startDate, endDate);
    }
  }, [active, startDate, endDate, rentalId, getFn]);

  const filteredActivity = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return activity;

    return activity.filter((item) => {
      const haystack = [
        getActivityLabel(item),
        getDeviceName(item),
        getRentalName(item),
        item.actor_label,
        item.actor_type,
        item.status,
        item.event_type,
        item.activity_type,
        item.error,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [activity, filter]);

  const visibleActivity = useMemo(
    () =>
      filteredActivity.slice(
        page * recordsPerPage,
        (page + 1) * recordsPerPage,
      ),
    [filteredActivity, page, recordsPerPage]
  );

  const rentalOptions = useMemo(
    () => [
      { value: "", label: "All Properties" },
      ...(rentals || []).map((rental) => ({
        value: String(rental.rental_id),
        label: rental.name || rental.pms_id || String(rental.rental_id),
      })),
    ],
    [rentals]
  );

  const handleFilterChange = useCallback((value) => {
    setPage(0);
    setFilter(value);
  }, []);

  const handleRentalChange = (value) => {
    setPage(0);
    setRentalId(value);
  };

  return (
    <li className="reports-tab-content">
      <h2>Activities</h2>
      <div className="uk-card hms-left-tabs-content-card uk-padding-remove">
        <FilterPanel
          searchTermPlaceholder="Device, property, person or event"
          onFilterChange={handleFilterChange}
          onSubmit={getFn}
        >
          <div className="uk-margin-small-left">
            <SearchableDropdown
              value={rentalId}
              onChange={handleRentalChange}
              placeholder="All Properties"
              options={rentalOptions}
            />
          </div>
        </FilterPanel>

        <div className="uk-overflow-auto">
          <table className="uk-table uk-table-hover uk-table-divider hms-table uk-margin-small-top">
            <thead>
              <tr>
                <th>ACTIVITY DATE</th>
                <th>EVENT</th>
                <th>DEVICE</th>
                <th>PROPERTY</th>
                <th>PERSON</th>
                <th>BATTERY</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                TableLoading(6)
              ) : visibleActivity.length ? (
                visibleActivity.map((item, idx) => {
                  const tone = getReportEventTone(item.activity_type);
                  const batteryLevel = item.payload?.device?.properties?.battery?.level;

                  return (
                    <tr key={item.id || item.seam_event_id || idx}>
                      <td>{formatTimestamp(item.occurred_at || item.created_at)}</td>
                      <td>
                        <span className={`seam-status-pill seam-status-${tone}`}>
                          {getActivityLabel(item)}
                        </span>
                      </td>
                      <td>{getDeviceName(item)}</td>
                      <td>{getRentalName(item)}</td>
                      <td>{item.actor_label || item.actor_type || emptyValue}</td>
                      <td>{formatBatteryLevel(batteryLevel)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="no-results">
                    No activity for this date range
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Flex alignment="between top" className="guest-form-top-filters">
        <Pagination
          count={filteredActivity.length}
          index={page}
          onIndexChanged={setPage}
        />
        {filteredActivity.length > 0 && <RecordsPerPage />}
      </Flex>
    </li>
  );
};

export default SmartLockActivities;
